import React, { ImgHTMLAttributes, useEffect, useMemo } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import isChromatic from 'chromatic';
import { isNil } from 'ramda';
import { ThemeType } from 'types/Theme';
import { getDevicePixelRatio } from '@daedalus/core/src/_web/utils/browser/getDevicePixelRatio';
import { ImageProvider } from '@daedalus/core/src/staticContent/types/ImageProvider';
import useIsImageloaded from '../../../hooks/useIsImageloaded';
import placeHolderImageSVG from '../../../images/placeholder-hotel-image.svg';
import { cssTheme } from '../../../themes';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
export type SizeType = 'lightbox' | 'extraLarge' | 'large' | 'main' | 'medium' | 'small' | 'mobileLarge' | 'mobileMedium' | 'mobileSmall' | 'gridPrimary' | 'gridSecondary';
export const SIZES: Record<SizeType, [number, number]> = {
  lightbox: [840, 635],
  extraLarge: [740, 393],
  large: [615, 340],
  main: [292, 284],
  medium: [59, 59],
  small: [38, 38],
  mobileLarge: [620, 176],
  mobileMedium: [620, 152],
  mobileSmall: [126, 152],
  gridPrimary: [456, 330],
  gridSecondary: [228, 162]
};
type BackgroundSize = 'cover' | 'contain';
export type BorderRadius = keyof ThemeType['layout']['radius'];
interface PropsType extends ImgHTMLAttributes<HTMLImageElement> {
  /** Apply specified border-radius styling. */
  borderRadius?: BorderRadius;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Pass through style to allow styles overrides */
  style?: React.CSSProperties;
  /** Whether to display the image as the full width of its parent */
  fullWidth?: boolean;
  /** Whether to show a skeleton element while image is loading */
  hasSkeletonLoader?: boolean;
  /** Override the height of the image. Does not work when "isResponsive" is "true" or when "size" is "main" */
  height?: number;

  /** Override the width of the image.*/
  width?: number;
  /** Provider used to manage resizing and serving of the images */
  imageProvider?: ImageProvider;
  /** Whether to display the image as the full width and height of its parent */
  isResponsive?: boolean;
  /** Size of the image */
  size?: SizeType;
  /** URL of the image */
  url: string;
  /** URL of the image place holder image incase of broken images */
  placeHolderImage?: string;
  /** background-size css property of a container having image as background */
  backgroundSize?: BackgroundSize;
  id?: string;
  /**
   * Callback function to handle image loading errors.
   * This function will be invoked when the image fails to load.
   */
  onImageError?: () => void;

  /**
   * Priority which determines how the image is loaded; High is load first, low is load last.
   */
  fetchPriority?: 'low' | 'high' | 'auto';

  /**
   *  Whether to use <img/> or background-image css styling
   */
  asImg?: boolean;
}
export const SkeletonElement = /*#__PURE__*/styled('div')({
  name: "SkeletonElement",
  class: "scvs1ob",
  propsAsIs: false
});
interface StyleProps {
  borderRadius?: BorderRadius;
  fullWidth?: boolean;
  imageHeight: number;
  imageWidth: number;
  shouldBeResponsive: boolean;
  imageSrc?: string;
  backgroundSize: BackgroundSize;
}
const _exp = /*#__PURE__*/() => ({
  shouldBeResponsive,
  fullWidth,
  imageWidth
}) => shouldBeResponsive || fullWidth ? '100%' : `${imageWidth}px`;
const _exp2 = /*#__PURE__*/() => ({
  shouldBeResponsive,
  imageHeight
}) => shouldBeResponsive ? '100%' : `${imageHeight}px`;
const _exp3 = /*#__PURE__*/() => ({
  imageSrc
}) => imageSrc ? `url(${imageSrc})` : 'none';
const _exp4 = /*#__PURE__*/() => ({
  backgroundSize
}) => backgroundSize;
const _exp5 = /*#__PURE__*/() => ({
  borderRadius = 'none'
}) => cssTheme.layout.radius[borderRadius!];
const BackgroundElement = /*#__PURE__*/styled('div')({
  name: "BackgroundElement",
  class: "b1fc33lo",
  propsAsIs: false,
  vars: {
    "b1fc33lo-0": [_exp()],
    "b1fc33lo-1": [_exp2()],
    "b1fc33lo-2": [_exp3()],
    "b1fc33lo-3": [_exp4()],
    "b1fc33lo-4": [_exp5()]
  }
});
const defaultImageProvider: ImageProvider = (url: string) => url;
export const Image = ({
  borderRadius,
  imageProvider = defaultImageProvider,
  size = 'main',
  url,
  isResponsive = false,
  fullWidth,
  hasSkeletonLoader = false,
  height,
  width,
  backgroundSize = 'cover',
  placeHolderImage,
  className,
  id,
  onImageError,
  style,
  fetchPriority,
  alt,
  loading,
  asImg = false,
  ...otherProps
}: PropsType) => {
  const dpr = getDevicePixelRatio(window);
  const extraProcessingParams = {
    dpr
  };
  const imageSrc = imageProvider(url, SIZES[size], extraProcessingParams);
  const {
    isLoaded,
    isErrored
  } = useIsImageloaded(imageSrc);
  useEffect(() => {
    if (isErrored && onImageError) {
      onImageError();
    }
  }, [isErrored, onImageError]);
  const shouldBeResponsive = isResponsive || size === 'main';
  const isGridPrimaryImage = size === 'gridPrimary';

  // eslint-disable-next-line prefer-const
  let [imageWidth, imageHeight] = SIZES[size];
  imageHeight = isNil(height) ? imageHeight : height;
  imageWidth = isNil(width) ? imageWidth : width;
  const imageElementImageSrc = useMemo(() => {
    if (isErrored || isChromatic()) return placeHolderImage ?? placeHolderImageSVG;
    return imageSrc;
  }, [isErrored, placeHolderImage, imageSrc]);

  /* eslint-disable react/no-unknown-property */
  return <BackgroundElement borderRadius={borderRadius} fullWidth={fullWidth} imageHeight={imageHeight} imageWidth={imageWidth} shouldBeResponsive={shouldBeResponsive} imageSrc={!asImg ? imageElementImageSrc : undefined} backgroundSize={backgroundSize} className={cx(className, isGridPrimaryImage && '--gridPrimary', Boolean(borderRadius) && '--borderRadius')} id={id} style={style}>
      {hasSkeletonLoader && !isLoaded && !isErrored && <SkeletonElement>
          <SkeletonLoader height={shouldBeResponsive ? undefined : imageHeight} />
        </SkeletonElement>}
      {asImg && <img src={imageElementImageSrc} alt={alt} loading={loading}
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Property 'fetchPriority' does not exist on type
    fetchPriority={fetchPriority} width="100%" height="100%" {...otherProps} />}
    </BackgroundElement>;
};

require("./Image.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Image.tsx");